import { type ReactNode, useEffect, useId, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';

const styles = stylex.create({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'oklch(0% 0 0 / 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 900,
  },
  panel: {
    backgroundColor: sentinel.surfaceElevated,
    color: sentinel.textBody,
    borderRadius: '0.75rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderEmphasis,
    boxShadow: sentinel.shadowThick,
    padding: '1.75rem',
    display: 'grid',
    gap: '1rem',
    maxHeight: 'calc(100vh - 2rem)',
    overflowY: 'auto',
  },
  narrow: { width: 'min(440px, calc(100vw - 2rem))' },
  medium: { width: 'min(560px, calc(100vw - 2rem))' },
  wide: { width: 'min(720px, calc(100vw - 2rem))' },
  title: {
    margin: 0,
    fontFamily: sentinel.fontDisplay,
    fontSize: '1.125rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  description: {
    margin: 0,
    fontSize: '0.875rem',
    color: sentinel.textMuted,
    lineHeight: 1.5,
  },
  buttonRow: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '0.25rem',
  },
});

export interface DialogShellProps {
  open: boolean;
  onCancel: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  width?: 'narrow' | 'medium' | 'wide';
  testId?: string;
  ariaLabelledBy?: string;
  /**
   * When false, overlay-click and Escape do NOT call `onCancel` — the only
   * way to close is via a control the consumer renders (e.g. a gated
   * footer button). Focus trap and focus restoration stay active.
   * Defaults to true (close on overlay/Escape).
   */
  dismissible?: boolean;
}

/**
 * Sentinel modal-dialog shell with overlay click-to-close, Escape to
 * close, focus trap, and focus restoration. Wraps the children in a
 * dark bice-tone panel matching the rest of the design system.
 *
 * Composes a form-aware footer via the optional `<DialogShell.Footer>`
 * helper exported below for the typical cancel/submit button row.
 */
export function DialogShell({
  open,
  onCancel,
  title,
  description,
  children,
  width = 'medium',
  testId,
  ariaLabelledBy,
  dismissible = true,
}: DialogShellProps) {
  const internalTitleId = useId();
  const titleId = ariaLabelledBy ?? internalTitleId;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      if (previousFocusRef.current) {
        try {
          previousFocusRef.current.focus();
        } catch {
          // ignored
        }
        previousFocusRef.current = null;
      }
      return;
    }
    previousFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    queueMicrotask(() => {
      const root = dialogRef.current;
      if (!root) return;
      const focusable = root.querySelector<HTMLElement>(
        'input, button, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!dismissible) return;
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(
          'input, button, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel, dismissible]);

  if (!open) return null;

  return (
    <div
      {...stylex.props(styles.overlay)}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        data-testid={testId}
        {...stylex.props(styles.panel, styles[width])}
      >
        {title ? (
          <h2 id={titleId} {...stylex.props(styles.title)}>
            {title}
          </h2>
        ) : null}
        {description ? <p {...stylex.props(styles.description)}>{description}</p> : null}
        {children}
      </div>
    </div>
  );
}

DialogShell.Footer = function DialogShellFooter({ children }: { children: ReactNode }) {
  return <div {...stylex.props(styles.buttonRow)}>{children}</div>;
};
